import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/app/components/ui/button';
import { getAllProjects, getAllNews, deleteProject, deleteNews, updateProject, updateNews, getAllCategories, updateCategories, createProject, createNews, type Category, seedDatabase, getAboutContent, updateAboutContent, type AboutContent, getContactInfo, updateContactInfo, type ContactInfo, getTeamMembers, type TeamMember, createTeamMember, updateTeamMember, deleteTeamMember, reorderProjects } from '@/services/api';
import type { Project } from '@/data/projects';
import type { NewsItem } from '@/data/news';
import { Pencil, Trash2, Plus, Upload, ChevronUp, ChevronDown, ChevronsUpDown, Home, ArrowUpDown } from 'lucide-react';
import { ProjectEditModal } from '@/app/components/admin/ProjectEditModal';
import { NewsEditModal } from '@/app/components/admin/NewsEditModal';
import { CategoryEditor } from '@/app/components/admin/CategoryEditor';
import { AdminTeam } from '@/app/components/admin/AdminTeam';
import { Settings } from '@/app/components/admin/Settings';
import { ReorderProjectsModal } from '@/app/components/admin/ReorderProjectsModal';
import { ExportImport } from '@/app/components/admin/ExportImport';
import AssetManager from '@/app/components/admin/AssetManager';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getSupabaseClient } from '@/utils/supabase/client';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { useNavigate } from 'react-router';

type ActiveTab = 'projects' | 'news' | 'categories' | 'profile' | 'contact' | 'team' | 'settings' | 'export-import' | 'assets';
type SortField = 'title' | 'category' | 'location' | 'year' | 'order';
type SortDirection = 'asc' | 'desc';

export function AdminDashboard() {
  const { user, signOut, accessToken, getAccessToken } = useAuth();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<ActiveTab>('projects');
  const [projects, setProjects] = useState<Project[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Edit modal states
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
  
  // Reorder modal state
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('🔄 Starting data load...');
      const [projectsData, newsData, categoriesData] = await Promise.all([
        getAllProjects(),
        getAllNews(),
        getAllCategories()
      ]);
      console.log('✅ Data loaded successfully:', { 
        projects: projectsData.length, 
        news: newsData.length, 
        categories: categoriesData.length 
      });
      setProjects(projectsData);
      setNews(newsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('❌ Error loading data:', error);
    } finally {
      console.log('🏁 Setting loading to false');
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleDeleteProject = async (id: string) => {
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    if (window.confirm(t('admin.deleteConfirm'))) {
      const success = await deleteProject(id, token);
      if (success) {
        setProjects(projects.filter(p => p.id !== id));
        alert('Projekt sikeresen törölve!');
      } else {
        alert('Hiba történt a projekt törlésekor.');
      }
    }
  };

  const handleDeleteNews = async (id: string) => {
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    if (window.confirm(t('admin.deleteConfirm'))) {
      const success = await deleteNews(id, token);
      if (success) {
        setNews(news.filter(n => n.id !== id));
        alert('Hír sikeresen törölve!');
      } else {
        alert('Hiba történt a hír törlésekor.');
      }
    }
  };

  const handleCreateProject = async (projectData: Omit<Project, 'id'>) => {
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    console.log('handleCreateProject called with:', projectData);
    
    const createdProject = await createProject(projectData, token);
    console.log('createProject returned:', createdProject);
    
    if (createdProject) {
      setProjects([...projects, createdProject]);
      setIsProjectModalOpen(false);
      setEditingProject(null);
      alert('Új projekt sikeresen létrehozva!');
    } else {
      alert('Hiba történt a projekt létrehozásakor. Nzd meg a böngésző konzolt a részletekért.');
    }
  };

  const handleSaveProject = async (updates: Partial<Project>) => {
    if (!editingProject) return;
    
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    console.log('handleSaveProject called with:', { updates, editingProject, token: token.substring(0, 20) + '...' });
    
    const updatedProject = await updateProject(editingProject.id, updates, token);
    console.log('updateProject returned:', updatedProject);
    
    if (updatedProject) {
      setProjects(projects.map(p => p.id === editingProject.id ? updatedProject : p));
      setIsProjectModalOpen(false);
      setEditingProject(null);
      alert('Projekt sikeresen frissítve!');
    } else {
      alert('Hiba történt a projekt frissítésekor. Nézd meg a böngésző konzolt a részletekért.');
    }
  };

  const handleCreateNews = async (newsData: Omit<NewsItem, 'id'>) => {
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    console.log('handleCreateNews called with:', newsData);
    
    const createdNews = await createNews(newsData, token);
    console.log('createNews returned:', createdNews);
    
    if (createdNews) {
      setNews([...news, createdNews]);
      setIsNewsModalOpen(false);
      setEditingNews(null);
      alert('Új hír sikeresen létrehozva!');
    } else {
      alert('Hiba történt a hír létrehozásakor. Nézd meg a böngésző konzolt a részletekért.');
    }
  };

  const handleSaveNews = async (updates: Partial<NewsItem>) => {
    if (!editingNews) return;
    
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    const updatedNews = await updateNews(editingNews.id, updates, token);
    if (updatedNews) {
      setNews(news.map(n => n.id === editingNews.id ? updatedNews : n));
      setIsNewsModalOpen(false);
      setEditingNews(null);
      alert('Hír sikeresen frissítve!');
    } else {
      alert('Hiba történt a hír frissítésekor.');
    }
  };

  const handleConvertAllNewsImages = async () => {
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }

    const externalNews = news.filter(n => !n.image.includes('supabase.co/storage'));
    
    if (externalNews.length === 0) {
      alert('Minden hír borítóképe már fel van töltve!');
      return;
    }

    if (!confirm(`${externalNews.length} hír bortóképét konvertáljuk át. Folytatod?`)) {
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const newsItem of externalNews) {
      try {
        console.log(`Converting image for news ${newsItem.id}:`, newsItem.image);
        
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-586f2897/convert-news-cover`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${publicAnonKey}`
            },
            body: JSON.stringify({
              imageUrl: newsItem.image,
              newsId: newsItem.id
            })
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Server error');
        }

        const { publicUrl } = await response.json();
        console.log(`✅ Converted successfully:`, publicUrl);

        const updatedNews = await updateNews(newsItem.id, { image: publicUrl }, token);
        
        if (updatedNews) {
          setNews(prevNews => prevNews.map(n => n.id === newsItem.id ? updatedNews : n));
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error(`Error converting image for news ${newsItem.id}:`, error);
        failCount++;
      }
    }

    alert(`Konverzió kész!\n✅ Sikeres: ${successCount}\n❌ Sikertelen: ${failCount}`);
  };

  const handleSaveCategories = async (updates: Category[]) => {
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    const updatedCategories = await updateCategories(updates, token);
    if (updatedCategories) {
      setCategories(updatedCategories);
      alert('Kategóriák sikeresen frissítve!');
    } else {
      alert('Hiba történt a kategóriák frissítésekor.');
    }
  };
  
  const handleAddProject = () => {
    setEditingProject(null);
    setIsProjectModalOpen(true);
  };
  
  const handleAddNews = () => {
    setEditingNews(null);
    setIsNewsModalOpen(true);
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setIsProjectModalOpen(true);
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    if (category) {
      return language === 'hu' ? category.name.hu : category.name.en;
    }
    return t(`category.${categoryId}`);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-black" />
      : <ChevronDown className="h-4 w-4 text-black" />;
  };

  const sortedProjects = useMemo(() => {
    const sorted = [...projects].sort((a, b) => {
      let compareValue = 0;
      
      switch (sortField) {
        case 'order':
          const orderA = a.order || 0;
          const orderB = b.order || 0;
          compareValue = orderA - orderB;
          break;
        case 'title':
          const titleA = language === 'hu' ? a.title.hu : a.title.en;
          const titleB = language === 'hu' ? b.title.hu : b.title.en;
          compareValue = titleA.localeCompare(titleB, language);
          break;
        case 'category':
          const catA = getCategoryName(a.category);
          const catB = getCategoryName(b.category);
          compareValue = catA.localeCompare(catB, language);
          break;
        case 'location':
          compareValue = a.location.localeCompare(b.location, language);
          break;
        case 'year':
          const yearA = parseInt(a.year) || 0;
          const yearB = parseInt(b.year) || 0;
          compareValue = yearA - yearB;
          break;
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
    
    return sorted;
  }, [projects, sortField, sortDirection, language, categories]);

  const [aboutContent, setAboutContent] = useState<AboutContent>({
    hu: '',
    en: ''
  });
  const [uploading, setUploading] = useState(false);
  const quillAboutHuRef = useRef<ReactQuill>(null);
  const quillAboutEnRef = useRef<ReactQuill>(null);

  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: 'info@zsalako.hu',
    phone: '+36 1 234 5678',
    address: 'Budapest, 1053 Kossuth Lajos utca 1.',
    officeLink: '',
    onlineText: '',
    companyName: '',
    companyLocation: '',
    taxNumber: '',
    registrationNumber: ''
  });

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    const fetchAboutContent = async () => {
      const content = await getAboutContent();
      console.log('AdminDashboard - Fetched about content:', content);
      console.log('AdminDashboard - About content HU length:', content?.hu?.length);
      console.log('AdminDashboard - About content EN length:', content?.en?.length);
      if (content) {
        setAboutContent(content);
      } else {
        console.warn('AdminDashboard - No about content returned from API');
      }
    };
    fetchAboutContent();

    const fetchContactInfo = async () => {
      const info = await getContactInfo();
      console.log('AdminDashboard - Fetched contact info:', info);
      if (info) {
        setContactInfo(info);
      } else {
        console.warn('AdminDashboard - No contact info returned from API');
      }
    };
    fetchContactInfo();

    const fetchTeamMembers = async () => {
      const members = await getTeamMembers();
      console.log('AdminDashboard - Fetched team members:', members);
      if (members) {
        setTeamMembers(members);
      } else {
        console.warn('AdminDashboard - No team members returned from API');
      }
    };
    fetchTeamMembers();
  }, []);

  const aboutImageHandler = async (isHungarian: boolean) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        setUploading(true);
        
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-586f2897/upload/about`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Server upload error:', errorData);
          throw new Error(errorData.details || errorData.error || 'Upload failed');
        }

        const { url } = await response.json();
        console.log('Image uploaded successfully:', url);

        const quillRef = isHungarian ? quillAboutHuRef : quillAboutEnRef;
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection();
          const position = range ? range.index : 0;
          quill.insertEmbed(position, 'image', url);
          quill.setSelection(position + 1);
        }

        setUploading(false);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Hiba történt a kép feltöltésekor: ' + (error as Error).message);
        setUploading(false);
      }
    };
  };

  const aboutModulesHu = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }], 
        ['bold', 'italic', 'underline', 'strike'], 
        [{ 'list': 'ordered'}, { 'list': 'bullet' }], 
        [{ 'indent': '-1'}, { 'indent': '+1' }], 
        ['link', 'image'], 
        [{ 'align': [] }], 
        ['clean']
      ],
      handlers: {
        image: () => aboutImageHandler(true)
      }
    }
  }), []);

  const aboutModulesEn = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }], 
        ['bold', 'italic', 'underline', 'strike'], 
        [{ 'list': 'ordered'}, { 'list': 'bullet' }], 
        [{ 'indent': '-1'}, { 'indent': '+1' }], 
        ['link', 'image'], 
        [{ 'align': [] }], 
        ['clean']
      ],
      handlers: {
        image: () => aboutImageHandler(false)
      }
    }
  }), []);

  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  }), []);

  const handleSaveAboutContent = async () => {
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    const success = await updateAboutContent(aboutContent, token);
    if (success) {
      alert('Rólunk szekció sikeresen frissítve!');
    } else {
      alert('Hiba történt a rólunk szekció frissítésekor.');
    }
  };

  const handleSaveContactInfo = async () => {
    const token = await getAccessToken();
    if (!token) {
      alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      return;
    }
    
    const success = await updateContactInfo(contactInfo, token);
    if (success) {
      alert('Kapcsolat információk sikeresen frissítve!');
    } else {
      alert('Hiba történt a kapcsolat információk frissítésekor.');
    }
  };

  const navigate = useNavigate();

  console.log('🎯 AdminDashboard render - loading:', loading, 'projects:', projects.length, 'news:', news.length, 'categories:', categories.length);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold">ZSALAKÓ ÉPÍTŐMŰHELY <span className="text-gray-500">/ ADMIN</span></h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
              >
                <Home className="h-4 w-4" />
                <span className="text-xs">Főoldal</span>
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <Button variant="outline" onClick={handleSignOut}>
                {t('admin.signOut')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-4 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('projects')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'projects'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('admin.projects')} ({projects.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'categories'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('admin.categories')} ({categories.length})
          </button>
          <button
            onClick={() => setActiveTab('news')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'news'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('admin.news')} ({news.length})
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'team'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Csapat ({teamMembers.length})
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'profile'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Profil
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'contact'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Kapcsolat
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'settings'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Beállítások
          </button>
          <button
            onClick={() => setActiveTab('export-import')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'export-import'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Export/Import
          </button>
          <button
            onClick={() => setActiveTab('assets')}
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'assets'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Aktív elemek
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-lg text-gray-500">Betöltés...</p>
          </div>
        ) : (
          <>
            {projects.length === 0 && news.length === 0 && categories.length === 0 && (
              <div className="mb-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                  Üres adatbázis észlelve
                </h3>
                <p className="text-yellow-700 mb-4">
                  Az adatbázis jelenleg üres. Kattints az alábbi gombra, hogy példa adatokkal (projektek, hírek, kategóriák és rólunk tartalom) töltsd fel.
                </p>
                <Button
                  onClick={async () => {
                    if (confirm('Biztosan inicializálni szeretnéd az adatbázist példa adatokkal?')) {
                      const success = await seedDatabase();
                      if (success) {
                        alert('Adatbázis sikeresen inicializálva! Az oldal újratöltődik...');
                        window.location.reload();
                      } else {
                        alert('Hiba történt az adatbázis inicializálásakor. Nézd meg a böngésző konzolt.');
                      }
                    }
                  }}
                >
                  🌱 Adatbázis inicializálása példa adatokkal
                </Button>
              </div>
            )}

            {activeTab === 'projects' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold">{t('admin.projects')}</h2>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setIsReorderModalOpen(true)}
                      variant="outline"
                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                    >
                      <ArrowUpDown className="mr-2 h-4 w-4" />
                      Projekt sorrend módosítása
                    </Button>
                    <Button onClick={handleAddProject}>
                      <Plus className="mr-2 h-4 w-4" />
                      {t('admin.addNew')}
                    </Button>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                          <button
                            onClick={() => handleSort('order')}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            Sorrend
                            <SortIcon field="order" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('title')}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            {t('admin.title')}
                            <SortIcon field="title" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('category')}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            {t('admin.category')}
                            <SortIcon field="category" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('location')}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            {t('admin.location')}
                            <SortIcon field="location" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('year')}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            {t('admin.year')}
                            <SortIcon field="year" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Műveletek
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedProjects.map((project) => (
                        <tr key={project.id}>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {project.order || 0}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{language === 'hu' ? project.title.hu : project.title.en}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getCategoryName(project.category)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {project.location}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {project.year}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEditProject(project)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteProject(project.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {projects.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-gray-500 mb-4">Még nincsenek projektek az adatbázisban.</p>
                      <Button onClick={handleAddProject} variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        Első projekt létrehozása
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'news' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold">{t('admin.news')}</h2>
                  <div className="flex gap-2">
                    {news.filter(n => !n.image.includes('supabase.co/storage')).length > 0 && (
                      <Button 
                        onClick={handleConvertAllNewsImages}
                        variant="outline"
                        className="text-orange-600 border-orange-600 hover:bg-orange-50"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Külső URL-ek konvertálása ({news.filter(n => !n.image.includes('supabase.co/storage')).length})
                      </Button>
                    )}
                    <Button onClick={handleAddNews}>
                      <Plus className="mr-2 h-4 w-4" />
                      {t('admin.addNew')}
                    </Button>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('admin.image')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('admin.title')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('admin.date')}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Műveletek
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {news.map((newsItem) => (
                        <tr key={newsItem.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <img
                              src={newsItem.image}
                              alt={newsItem.title.hu}
                              className="h-12 w-12 object-cover rounded"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{newsItem.title.hu}</div>
                            <div className="text-sm text-gray-500">{newsItem.title.en}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(newsItem.date).toLocaleDateString('hu-HU')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => {
                                setEditingNews(newsItem);
                                setIsNewsModalOpen(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteNews(newsItem.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {news.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-gray-500 mb-4">Még nincsenek hírek az adatbázisban.</p>
                      <Button onClick={handleAddNews} variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        Első hír létrehozása
                      </Button>
                    </div>
                  )}
                </div>
                
                {news.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Tipp:</strong> Kattints a ceruza ikonra egy hír szerkesztéséhez, vagy a "+ Új hozzáadása" gombra új hír létrehozásához.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'categories' && (
              <div>
                <h2 className="text-2xl font-semibold mb-6">Kategóriák</h2>
                
                <CategoryEditor
                  categories={categories}
                  onSave={handleSaveCategories}
                />
              </div>
            )}

            {activeTab === 'profile' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold">Profil</h2>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Szöveg (Magyar)
                      </label>
                      <div className="border border-gray-300 rounded-md" style={{ height: '300px' }}>
                        <ReactQuill
                          key="about-hu-editor"
                          ref={quillAboutHuRef}
                          theme="snow"
                          value={aboutContent.hu}
                          onChange={(value) => {
                            setAboutContent(prev => ({ ...prev, hu: value }));
                          }}
                          modules={aboutModulesHu}
                          style={{ height: '250px' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Szöveg (English)
                      </label>
                      <div className="border border-gray-300 rounded-md" style={{ height: '300px' }}>
                        <ReactQuill
                          key="about-en-editor"
                          ref={quillAboutEnRef}
                          theme="snow"
                          value={aboutContent.en}
                          onChange={(value) => {
                            setAboutContent(prev => ({ ...prev, en: value }));
                          }}
                          modules={aboutModulesEn}
                          style={{ height: '250px' }}
                        />
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-md mt-20">
                      <p className="text-sm text-blue-800">
                        <strong>Képek beszúrása:</strong> Kattints a szerkesztő eszköztárában a kép ikonra, majd válassz egy képet a számítógépedről. 
                        A kép automatikusan feltöltődik és beillesztésre kerül a szövegbe.
                      </p>
                      <p className="text-sm text-blue-800 mt-2">
                        <strong>Linkek hozzáadása:</strong> Jelöld ki a szöveget, majd kattints a lánc ikonra és add meg az URL-t.
                      </p>
                    </div>
                    
                    <Button onClick={handleSaveAboutContent} disabled={uploading}>
                      {t('admin.save')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold">Kapcsolat</h2>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Kapcsolat információk</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          E-mail cím
                        </label>
                        <input
                          type="email"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          value={contactInfo.email}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Telefonszám
                        </label>
                        <input
                          type="tel"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          value={contactInfo.phone}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Iroda cím
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          value={contactInfo.address}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, address: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Iroda link (opcionális)
                        </label>
                        <input
                          type="url"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="https://maps.google.com/..."
                          value={contactInfo.officeLink || ''}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, officeLink: e.target.value }))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Ha kitöltöd, az iroda cím klikkelhetővé válik a weboldalon (pl. Google Maps link)
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Online szöveg (opcionális, több soros RTF)
                        </label>
                        <ReactQuill
                          theme="snow"
                          value={contactInfo.onlineText || ''}
                          onChange={(value) => setContactInfo(prev => ({ ...prev, onlineText: value }))}
                          modules={quillModules}
                          className="bg-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Online tartalom formázott szöveggel (linkek, felsorolás, stb.)
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold mb-4">Cégadatok</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.companyName')}
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="pl. ZSALAKÓ ÉPÍTŐMŰHELY KFT."
                          value={contactInfo.companyName || ''}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, companyName: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.companyLocation')}
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="pl. 4033 Debrecen, Herpay Gábor u. 18."
                          value={contactInfo.companyLocation || ''}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, companyLocation: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.taxNumber')}
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="pl. 20388324-2-09"
                          value={contactInfo.taxNumber || ''}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, taxNumber: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.registrationNumber')}
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="pl. 4545456645"
                          value={contactInfo.registrationNumber || ''}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, registrationNumber: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <Button onClick={handleSaveContactInfo}>
                    {t('admin.save')}
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'team' && (
              <div>
                <AdminTeam
                  members={teamMembers}
                  onMembersChange={setTeamMembers}
                  onCreate={createTeamMember}
                  onUpdate={updateTeamMember}
                  onDelete={deleteTeamMember}
                  getAccessToken={getAccessToken}
                />
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <Settings />
              </div>
            )}

            {activeTab === 'export-import' && (
              <div>
                <ExportImport getAccessToken={getAccessToken} />
              </div>
            )}

            {activeTab === 'assets' && (
              <div>
                <AssetManager />
              </div>
            )}
          </>
        )}
      </main>

      <ProjectEditModal
        isOpen={isProjectModalOpen}
        onClose={() => {
          setIsProjectModalOpen(false);
          setEditingProject(null);
        }}
        project={editingProject}
        onUpdate={handleSaveProject}
        onCreate={handleCreateProject}
        categories={categories}
      />

      <NewsEditModal
        isOpen={isNewsModalOpen}
        onClose={() => {
          setIsNewsModalOpen(false);
          setEditingNews(null);
        }}
        newsItem={editingNews}
        onUpdate={handleSaveNews}
        onCreate={handleCreateNews}
      />

      <ReorderProjectsModal
        isOpen={isReorderModalOpen}
        projects={projects}
        language={language}
        onClose={() => setIsReorderModalOpen(false)}
        onSave={async (projectIds) => {
          const token = await getAccessToken();
          if (!token) {
            alert('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
            return;
          }

          const success = await reorderProjects(projectIds, token);
          if (success) {
            await loadData();
            setIsReorderModalOpen(false);
            alert('Projekt sorrend sikeresen frissítve!');
          } else {
            alert('Hiba történt a projekt sorrend frissítésekor.');
          }
        }}
      />
    </div>
  );
}